import { ComponentProps, useCallback, useEffect, useState, type JSX } from 'react';
import LightboxAd from '@alltrails/ads/components/LightboxAd/LightboxAd';
import type { LightboxItem, CreditLinkResponse } from '@alltrails/denali/types';
import { get } from '@alltrails/core/utils/get';
import { ModalPortal } from '@alltrails/core';
import { useExperiments } from '@alltrails/experiments';
import LightboxModal from '../components/LightboxModal/LightboxModal';

export type LightboxProps = {
  photos: LightboxItem[];
  closeModal: () => void;
  trailId?: string | number;
  isStaticPhoto?: boolean;
  showTimestampOnly?: boolean;
} & Pick<
  ComponentProps<typeof LightboxModal>,
  | 'analyticsReportLocation'
  | 'context'
  | 'hidePageScrollbarGutter'
  | 'languageRegionCode'
  | 'onPhotoViewed'
  | 'onPhotosPageLinkClick'
  | 'photosPageLinkInfo'
  | 'onUserClick'
  | 'startingIndex'
>;

const Lightbox = ({ photos, closeModal, trailId, isStaticPhoto, startingIndex, showTimestampOnly, ...modalProps }: LightboxProps): JSX.Element => {
  const [clickCount, setClickCount] = useState(0);
  const [photoIndex, setPhotoIndex] = useState(startingIndex);
  const experiment = useExperiments()?.['web-growth-native-ads-v2'];

  useEffect(() => setPhotoIndex(startingIndex), [startingIndex]);

  const creditPhotoUrl = useCallback(
    (index: number) => {
      const url = `/photos/${photos[index].id}/credit-link`;
      const params = new URLSearchParams();

      if (trailId) {
        params.set('provide_set_as_default', 'true');
      } else {
        params.set('provide_trail_links', 'true');
      }

      return `${url}?${params.toString()}`;
    },
    [trailId, photos]
  );

  const fetchPhotoDetails = async (newPhotoIndex: number) => {
    try {
      let response;
      if (!isStaticPhoto) {
        response = await get<CreditLinkResponse>(creditPhotoUrl(newPhotoIndex));
      }
      if (isStaticPhoto || !response) {
        return photos[newPhotoIndex.toString()];
      }
      return response;
    } catch (error) {
      throw new Error(`Error getting a photo details: ${error}`);
    }
  };

  return (
    <ModalPortal>
      <LightboxModal
        {...modalProps}
        images={photos}
        onClose={closeModal}
        onNavigate={fetchPhotoDetails}
        startingIndex={photoIndex}
        setCount={setClickCount}
        shouldHideActions={isStaticPhoto}
        showTimestampOnly={showTimestampOnly}
      />
      {(experiment?.value === 'unpaid_only' || experiment?.value === 'unpaid-and-paid') && (
        <LightboxAd count={clickCount} experiment={experiment?.value} />
      )}
    </ModalPortal>
  );
};

export default Lightbox;
